/**
 * admin/src/pages/OverviewPage.tsx
 * Dashboard tổng quan v3.0 — Stats + Activity Feed + Quick Actions
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useStats } from '../hooks/useStats';
import { adminFetch } from '../hooks/useAdmin';
import {
  StatCard,
  ErrorBanner,
  LoadingSpinner,
  formatVND,
} from '../components/ui/index';

// ─── Mini Bar Chart (no library needed) ──────────────────────────────────────

function MiniBar({ values, color = 'var(--accent)' }: { values: number[]; color?: string }) {
  const max = Math.max(...values, 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 32, marginTop: 8 }}>
      {values.map((v, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            height: `${Math.max((v / max) * 100, v > 0 ? 8 : 0)}%`,
            background: i === values.length - 1 ? color : `${color}55`,
            borderRadius: '2px 2px 0 0',
            minWidth: 3,
            transition: 'height 0.3s ease',
          }}
          title={String(v)}
        />
      ))}
    </div>
  );
}

// ─── Quick Actions ────────────────────────────────────────────────────────────

const QUICK_ACTIONS = [
  { icon: '👤', label: 'Đổi tier user',   desc: 'Nâng/hạ cấp tài khoản',     to: '/admin/users' },
  { icon: '📧', label: 'Gửi email',       desc: 'Thông báo hàng loạt',         to: '/admin/notifications' },
  { icon: '✍️', label: 'Bài viết mới',   desc: 'Tạo bài blog',                to: '/admin/blog/new' },
  { icon: '📊', label: 'Xem Analytics',  desc: 'Thống kê chi tiết',            to: '/admin/analytics' },
  { icon: '⚙️', label: 'Settings',       desc: 'Cài đặt hệ thống',            to: '/admin/settings' },
  { icon: '💬', label: 'Feedback',       desc: 'Phản hồi từ người dùng',      to: '/admin/feedback' },
] as const;

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function StatGridSkeleton() {
  return (
    <div className="stat-grid">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="stat-card skeleton" style={{ minHeight: 110 }} />
      ))}
    </div>
  );
}

// ─── ClearCache Button ────────────────────────────────────────────────────────

type CacheState = 'idle' | 'loading' | 'success' | 'error';

function ClearCacheButton() {
  const [state, setState] = useState<CacheState>('idle');
  const [message, setMessage] = useState('');

  const handleClear = async () => {
    if (state === 'loading') return;
    setState('loading');
    setMessage('');

    try {
      const res = await adminFetch<{ ok: boolean; deleted: number; error?: string }>(
        '/cache/invalidate',
        { method: 'POST' }
      );
      if (res.ok) {
        setState('success');
        setMessage(res.deleted === 0 ? 'Cache đã sạch' : `Đã xóa ${res.deleted} key`);
      } else {
        setState('error');
        setMessage(res.error ?? 'Lỗi không xác định');
      }
    } catch {
      setState('error');
      setMessage('Không kết nối được server');
    }

    setTimeout(() => { setState('idle'); setMessage(''); }, 4000);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <button
        onClick={handleClear}
        disabled={state === 'loading'}
        className="btn btn-secondary btn-sm"
        style={{
          ...(state === 'success' ? { borderColor: 'var(--success)', color: 'var(--success)' } : {}),
          ...(state === 'error'   ? { borderColor: 'var(--danger)',  color: 'var(--danger)'  } : {}),
        }}
        title="Xóa toàn bộ KV Cache"
      >
        {state === 'loading' ? <LoadingSpinner size={13} /> :
         state === 'success' ? '✓' :
         state === 'error'   ? '✕' : '⌫'}
        {state === 'loading' ? 'Đang xóa…' : 'Xóa Cache'}
      </button>
      {message && (
        <span style={{
          fontSize: 12.5,
          fontWeight: 500,
          color: state === 'success' ? 'var(--success)' : 'var(--danger)',
        }}>
          {message}
        </span>
      )}
    </div>
  );
}

// ─── Alert Banner ─────────────────────────────────────────────────────────────

function AlertBanner({ icon, message, color }: { icon: string; message: string; color: string }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '12px 16px',
      background: `${color}15`,
      border: `1px solid ${color}40`,
      borderLeft: `3px solid ${color}`,
      borderRadius: 'var(--radius-lg)',
      marginBottom: 20,
      fontSize: 14,
      color: 'var(--text-1)',
    }}>
      <span style={{ fontSize: 18 }}>{icon}</span>
      <span style={{ flex: 1 }}>{message}</span>
      <button
        onClick={() => setDismissed(true)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 16 }}
      >✕</button>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function OverviewPage() {
  const { stats, isLoading, error, refetch } = useStats();

  const now = new Date();
  const hour = now.getHours();
  const timeGreeting = hour < 6 ? 'Buổi khuya' : hour < 12 ? 'Chào buổi sáng' : hour < 18 ? 'Chào buổi chiều' : 'Chào buổi tối';
  const timeEmoji = hour < 6 ? '🌙' : hour < 12 ? '☀️' : hour < 18 ? '🌤️' : '🌙';

  // Simulated sparkline data (7 ngày gần nhất)
  const mockWeekly = [12, 8, 19, 15, 22, 18, stats?.students?.active_today ?? 0];

  return (
    <div>
      {/* ── Header ── */}
      <div className="page-header">
        <div className="page-header__left">
          <h1>{timeGreeting} {timeEmoji}</h1>
          <p>
            Dashboard · luyenthi.io.vn ·{' '}
            {now.toLocaleDateString('vi-VN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <ClearCacheButton />
      </div>

      {/* ── Alerts ── */}
      {stats && stats.users.new_today > 10 && (
        <AlertBanner
          icon="🎉"
          color="var(--success)"
          message={`Hôm nay có ${stats.users.new_today} users mới đăng ký — ngày hoạt động tốt!`}
        />
      )}
      {stats && stats.students.active_today === 0 && (
        <AlertBanner
          icon="⚠️"
          color="var(--warning)"
          message="Chưa có học sinh active hôm nay. Cân nhắc gửi email nhắc nhở."
        />
      )}

      {/* ── Error ── */}
      {error && (
        <div style={{ marginBottom: 24 }}>
          <ErrorBanner message={error} onRetry={refetch} />
        </div>
      )}

      {/* ── Stats Grid ── */}
      {isLoading ? (
        <StatGridSkeleton />
      ) : stats ? (
        <div className="stat-grid">
          <StatCard
            label="Tổng Users"
            icon="👤"
            value={stats.users.total}
            trendValue={`+${stats.users.new_today} hôm nay`}
            trend="up"
          />
          <StatCard label="Free"    icon="🔓" value={stats.users.free ?? 0}    sub="tài khoản miễn phí" />
          <StatCard label="Member"  icon="🏅" value={stats.users.basic ?? 0}   sub="gói member" />
          <StatCard label="Premium" icon="⭐" value={stats.users.premium ?? 0} sub="gói cao cấp" />
          <StatCard
            label="Học sinh"
            icon="🎓"
            value={stats.students.total}
            sub={`${stats.students.active_7d} active 7 ngày qua`}
          />
          <StatCard
            label="Active hôm nay"
            icon="🔥"
            value={stats.students.active_today}
            sub="học sinh hoạt động"
            trend={stats.students.active_today > 0 ? 'up' : undefined}
          />
          <StatCard
            label="Quiz hôm nay"
            icon="📝"
            value={stats.quizAttempts.today}
            sub={`${stats.quizAttempts.week} bài tuần này`}
          />
          <StatCard
            label="Doanh thu ước tính"
            icon="💰"
            value={formatVND(stats.revenue.estimate_monthly)}
            sub="tháng hiện tại"
          />
        </div>
      ) : null}

      {/* ── 2-column layout: Quick Actions + Activity ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 8 }}>
        {/* Quick Actions */}
        <div>
          <div style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.09em',
            color: 'var(--text-3)',
            marginBottom: 14,
          }}>
            Truy cập nhanh
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {QUICK_ACTIONS.map(({ to, icon, label, desc }) => (
              <Link
                key={to}
                to={to}
                style={{ textDecoration: 'none' }}
              >
                <div
                  className="card"
                  style={{
                    padding: '14px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                    (e.currentTarget as HTMLDivElement).style.boxShadow = 'var(--shadow-md)';
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border-focus)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.transform = '';
                    (e.currentTarget as HTMLDivElement).style.boxShadow = '';
                    (e.currentTarget as HTMLDivElement).style.borderColor = '';
                  }}
                >
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{icon}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text-1)', marginBottom: 2 }}>
                      {label}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{desc}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Activity Feed */}
        <div>
          <div style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.09em',
            color: 'var(--text-3)',
            marginBottom: 14,
          }}>
            Hoạt động học sinh (7 ngày)
          </div>
          <div className="card" style={{ padding: '20px 24px' }}>
            {/* Mini bar chart */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Học sinh active</span>
              <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-1)' }}>
                {stats?.students.active_7d ?? '—'}
              </span>
            </div>
            <MiniBar values={mockWeekly} color="var(--accent)" />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: 'var(--text-3)' }}>
              <span>7 ngày trước</span>
              <span>Hôm nay</span>
            </div>

            <div className="section-divider" style={{ margin: '16px 0' }} />

            {/* Tier distribution */}
            {stats && (
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 10, fontWeight: 600 }}>
                  Phân bổ tier
                </div>
                {[
                  { label: 'Free', value: stats.users.free ?? 0, color: 'var(--tier-free)', total: stats.users.total },
                  { label: 'Member', value: stats.users.basic ?? 0, color: 'var(--tier-basic)', total: stats.users.total },
                  { label: 'Premium', value: stats.users.premium ?? 0, color: 'var(--tier-premium)', total: stats.users.total },
                ].map(({ label, value, color, total }) => {
                  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                  return (
                    <div key={label} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12.5 }}>
                        <span style={{ color: 'var(--text-2)', fontWeight: 500 }}>{label}</span>
                        <span style={{ color: 'var(--text-3)' }}>{value.toLocaleString('vi-VN')} ({pct}%)</span>
                      </div>
                      <div style={{ height: 6, background: 'var(--bg-elevated)', borderRadius: 99, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${pct}%`,
                          background: color,
                          borderRadius: 99,
                          transition: 'width 0.6s ease',
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
